import store from "@/store/BookingStore";
import { TypedStore } from "@/store/BookingStore.types";
import { PrestType } from "../../../requests-module/src/booking-state/BookingV2";

// Generic DataLayer event interface
export interface DataLayerEvent {
    [key: string]: any;
}

// Builder class for constructing dataLayer events
export class DataLayerEventBuilder {
    private eventData: DataLayerEvent = {};

    constructor(private store: TypedStore) {
        // Ensure dataLayer array exists
        if (!window.dataLayer) {
            window.dataLayer = [];
        }
    }

    // Set the event name
    event(name: string): this {
        this.eventData.event = name;
        return this;
    }

    // Generic parameter setter
    param(key: string, value: any): this {
        // include null/empty strings explicitly if needed
        this.eventData[key] = value;
        return this;
    }

    // Add ecommerce items (full item objects)
    addEcommerceItems(items: any[]): this {
        this.eventData.ecommerce = { items };
        return this;
    }

    // Build the final object
    build(): DataLayerEvent {
        return this.eventData;
    }

    // Push directly to window.dataLayer
    push(): void {
        window.dataLayer.push(this.build());
    }
}

// Generic tracker function
function trackEvent(
    eventName: string,
    config?: (b: DataLayerEventBuilder) => void
): void {
    const builder = new DataLayerEventBuilder(store as TypedStore);
    builder.event(eventName);
    if (config)
        config(builder);
    builder.push();
}

// Helper: calculate duration (simple days diff)
export function calculateDuration(start?: string, end?: string): string {
    if (!start || !end) return '';
    const s = new Date(start);
    const e = new Date(end);
    const diff = Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
    return diff.toString();
}

// ---------- Event-specific functions ---------- //

// 1. Page View
export function trackPageView(): void {
    trackEvent('page_view', (b) => {
        const product = store.getters.getProduct;
        const user = store.getters.getLoggedUser;
        b
            .param('pageName', window.location.pathname)
            .param('pageType', document.body.dataset.pageType || '')
            .param('page_category', '') // TODO: map 1th level category
            .param('page_category2', '') // TODO: map 2th level category
            .param('page_category3', '') // TODO: map 3th level category
            .param('page_category4', '') // TODO: map 4th level category
            .param('country', store.getters.getLocale)
            .param('user_logged', !!user)
            .param('user_id', user?.iresa_id || '')
            .param('new_customer', false) // TODO: implement new customer logic
            .param('user_mail_sha256', null);
    });
}

// 2. View Item
export function trackViewItem(): void {
    trackEvent('view_item', (b) => {
        const booking = store.getters.getBooking;
        const product = store.getters.getProduct;
        const item = {
            item_LiheId: product?.liheId || '',
            item_LiheName: product?.name || '',
            item_PrestIds: booking?.options?.map(o => o.prestId) || [],
            item_PrestNames: booking?.options?.map(o => o.name) || [],
            item_price_ati: booking?.sellingPrice || 0,
            item_price_tf: booking?.purchasePrice || 0,
            item_category: product?.station.stationNameFr || '',
            item_category2: product?.station.stationNameEn || '',
            item_category3: product?.station.stationNameNl || '',
            item_category4: '', // TODO
            item_variant: '', // TODO
            item_ville_name: product?.station.stationNameFr || '',
            item_ville_id: product?.stationId || '',
            item_region_name: '', // TODO:  product?.station.regionName
            item_region_id: '',// TODO:  product?.station.regionId
            item_pays_name: product?.country || '',
            item_pays_id: product?.country || '',
            item_begin_date: booking?.departureDate || '',
            item_end_date: booking?.returnDate || '',
            item_duration: calculateDuration(booking?.departureDate, booking?.returnDate),
            item_reviews_note: '', // TODO: map review score
            item_reviews: '' // TODO: map review count
        };
        b.addEcommerceItems([item]);
    });
}

// 3. View Item List
export function trackViewItemList(): void {
    trackEvent('view_item_list', (b) => {
        const booking = store.getters.getBooking;
        const product = store.getters.getProduct;
        const items = (booking?.options || []).map((o, idx) => ({
            item_LiheId: product?.liheId || '',
            item_LiheName: product?.name || '',
            item_PrestIds: [o.prestId],
            item_PrestNames: [o.name],
            item_price_ati: o.sellingPrice || 0,
            item_price_tf: o.purchasePrice || 0,
            item_category: product?.station.stationNameFr || '',
            item_category2: product?.station.stationNameEn || '',
            item_category3: product?.station.stationNameNl || '',
            item_category4: '',
            item_variant: '',
            item_ville_name: product?.station.stationNameFr || '',
            item_ville_id: product?.stationId || '',
            item_region_name: '', // TODO:  product?.station.regionName, 
            item_region_id: '', // TODO:  product?.station.regionId, 
            item_pays_name: product?.country || '',
            item_pays_id: product?.country || '',
            item_begin_date: booking?.departureDate || '',
            item_end_date: booking?.returnDate || '',
            item_duration: calculateDuration(booking?.departureDate, booking?.returnDate),
            item_reviews_note: '',
            item_reviews: '',
            position: idx + 1
        }));
        b.addEcommerceItems(items);
    });
}

// 4. Item Click
export function trackItemClick(optionId: string): void {
    trackEvent('item_click', (b) => {
        const booking = store.getters.getBooking;
        const product = store.getters.getProduct;
        const o = booking?.options?.find(x => x.id === optionId);
        const item = o ? {
            item_LiheId: product?.liheId || '',
            item_LiheName: product?.name || '',
            item_PrestIds: [o.prestId],
            item_PrestNames: [o.name],
            item_price_ati: o.sellingPrice || 0,
            item_price_tf: o.purchasePrice || 0,
            item_category: product?.station.stationNameFr || '',
            item_category2: product?.station.stationNameEn || '',
            item_category3: product?.station.stationNameNl || '',
            item_category4: '',
            item_variant: '',
            item_ville_name: product?.station.stationNameFr || '',
            item_ville_id: product?.stationId || '',
            item_region_name: '', // TODO:  product?.station.regionName,
            item_region_id: '', // TODO:  product?.station.regionId, 
            item_pays_name: product?.country || '',
            item_pays_id: product?.country || '',
            item_begin_date: booking?.departureDate || '',
            item_end_date: booking?.returnDate || '',
            item_duration: calculateDuration(booking?.departureDate, booking?.returnDate),
            item_reviews_note: '',
            item_reviews: '',
            position: 1
        } : {};
        b.addEcommerceItems([item]);
    });
}

// 5. Add to Cart
export function trackAddToCart(): void {
    trackEvent('add_to_cart', (b) => {
        const booking = store.getters.getBooking;
        const o = store.getters.getLastSelectedOption
        const item = o ? {
            item_quantity: o.quantity || 0,
            item_PrestIds: [o.prestId],
            item_PrestNames: [o.name],
            item_price_ati: o.sellingPrice || 0,
            item_price_tf: o.purchasePrice || 0,
            item_begin_date: booking?.departureDate || '',
            item_end_date: booking?.returnDate || '',
            item_duration: calculateDuration(booking?.departureDate, booking?.returnDate),
            item_LiheId: booking?.liheId || '',
            item_LiheName: booking?.liheName || '',
            item_season: '',
            item_ville_name: booking?.stationName || '',
            item_ville_id: booking?.placeId || '',
            item_region_name: '',
            item_region_id: '',
            item_pays_name: '',
            item_pays_id: '',
            item_reviews_note: '',
            item_reviews: ''
        } : {};
        b.addEcommerceItems([item]);
    });
}

// 6. Ask Quote
export function trackAskQuote(): void {
    trackEvent('ask_quote');
}

// 7. Book
export function trackBook(): void {
    trackEvent('book');
}

// 8. Search Module
export function trackSearchModule(): void {
    trackEvent('search_module', (b) => {
        const booking = store.getters.getBooking;
        b
            .param('search_event', booking?.origin || '')
            .param('search_begin_date', booking?.departureDate || '')
            .param('search_end_date', booking?.returnDate || '')
            .param('search_capacity', booking?.numberOfPax || '')
            .param('search_package', booking?.pack || '')
            .param('search_lodging_type', store.getters.getProduct?.lodgingTypeId || '');
    });
}

// 9. Account Creation
export function trackAccountCreation(): void {
    trackEvent('account_creation', (b) => {
        b.param('optin_newsletter', store.getters.getIsSubscribeNewsletter);
    });
}

// 10. Filter Click
export function trackFilterClick(category: string, name: string): void {
    trackEvent('filter_click', (b) => {
        b.param('filter_category', category).param('filter_name', name);
    });
}

// 11. Begin Checkout
export function trackBeginCheckout(): void {
    trackEvent('begin_checkout', (b) => {
        // map cart and pricing fields
        const booking = store.getters.getBooking;
        b
            .param('cart_id', booking?.boReference || '')
            .param('cart_amount_ati', booking?.sellingPrice || 0)
            .param('cart_amount_tf', booking?.purchasePrice || 0)
            .param('cart_amount_ati_without_fees', calculateWithoutFees() || 0)
            .param('cart_amount_tf_without_fees', calculateWithoutFees() || 0)
            .param('cart_discount_amount_ati', '')
            .param('fees', calculateFees() || 0)
            .param('promocode', booking?.promocode?.code || '');
        // include items
        trackCartItems(b);
    });
}

// Shared helper to map full ecommerce items for cart/update/etc
function trackCartItems(b: DataLayerEventBuilder) {
    const booking = store.getters.getBooking;
    const product = store.getters.getProduct;
    const services = store.getters.getServicesInfo;
    const items = (booking?.options || []).filter(o => o.quantity > 0 && o.prestTypeId != PrestType.Fees).map(o => {
        const info = services?.find(s => s.id == o.id);
        return {
            item_LiheId: product?.liheId || '',
            item_LiheName: product?.name || '',
            item_PrestIds: [o.prestId],
            item_PrestNames: [info?.name || o.name],
            item_quantity: o.quantity || 0,
            item_price_ati: o.sellingPrice || 0,
            item_price_tf: o.purchasePrice || 0,
            item_category: product?.station.stationNameFr || '',
            item_category2: product?.station.stationNameEn || '',
            item_category3: product?.station.stationNameNl || '',
            item_category4: '',
            item_variant: '',
            item_ville_name: product?.station.stationNameFr || '',
            item_ville_id: product?.stationId || '',
            item_region_name: '', // TODO:  product?.station.regionName
            item_region_id: '',// TODO:  product?.station.regionId
            item_pays_name: product?.country || '',
            item_pays_id: product?.country || '',
            item_begin_date: booking?.departureDate || '',
            item_end_date: booking?.returnDate || '',
            item_duration: calculateDuration(booking?.departureDate, booking?.returnDate),
            item_reviews_note: '',
            item_reviews: ''
        }
    });
    b.addEcommerceItems(items);
}

// 12. Cart Update
export function trackCartUpdate(): void {
    trackEvent('cart_update', (b) => {
        const booking = store.getters.getBooking;
        b
            .param('cart_id', booking?.boReference || '')
            .param('cart_amount_ati', booking?.sellingPrice || 0)
            .param('cart_amount_tf', booking?.purchasePrice || 0)
            .param('cart_amount_ati_without_fees', calculateWithoutFees() || 0)
            .param('cart_amount_tf_without_fees', calculateWithoutFees() || 0)
            .param('cart_discount_amount_ati', '')
            .param('fees', calculateFees() || 0)
            .param('promocode', booking?.promocode?.code || '');
        trackCartItems(b);
    });
}

// 13. Login Step 1
export function trackLoginStep1(): void {
    trackEvent('login_step1', (b) => {
        trackCartContext(b);
    });
}

// 14. Login Step 2
export function trackLoginStep2(): void {
    trackEvent('login_step2', (b) => {
        trackCartContext(b);
    });
}

// 15. User Infos
export function trackUserInfos(): void {
    trackEvent('user_infos', (b) => {
        trackCartContext(b);
    });
}

// 16. Payment
export function trackPayment(): void {
    trackEvent('payment', (b) => {
        trackCartContext(b);
    });
}

// 17. Purchase
export function trackPurchase(): void {
    trackEvent('purchase', (b) => {
        const booking = store.getters.getBooking;
        b
            .param('transaction_id', booking?.boReference || '')
            .param('cart_id', booking?.boReference || '')
            .param('cart_amount_ati', booking?.sellingPrice || 0)
            .param('cart_amount_tf', booking?.purchasePrice || 0)
            .param('cart_amount_ati_without_fees', calculateWithoutFees() || 0)
            .param('cart_amount_tf_without_fees', calculateWithoutFees() || 0)
            .param('deposit_amount_ati', '')
            .param('deposit_amount_tf', '')
            .param('fees', calculateFees() || 0)
            .param('promocode', booking?.promocode?.code || '')
            .param('cart_discount_amount_ati', '')
            .param('cart_discount_amount_tf', '')
            .param('payment_method', '');
        trackCartItems(b);
    });
}

// 18. Quote Creation
export function trackQuoteCreation(): void {
    trackEvent('quote_creation', (b) => {
        const booking = store.getters.getBooking;
        b
            .param('quote_id', booking?.boReference || '')
            .param('cart_id', booking?.boReference || '')
            .param('cart_amount_ati', booking?.sellingPrice || 0)
            .param('cart_amount_tf', booking?.purchasePrice || 0)
            .param('cart_amount_ati_without_fees', calculateWithoutFees() || 0)
            .param('cart_amount_tf_without_fees', calculateWithoutFees() || 0)
            .param('fees', calculateFees() || 0)
            .param('promocode', booking?.promocode?.code || '');
        trackCartItems(b);
    });
}

// 19. Add to Cart Push Cart
export function trackAddToCartPushCart(): void {
    trackEvent('add_to_cart_push_cart', (b) => {
        const lastOption = store.getters.getLastSelectedOption;
        if (lastOption) {
            b.addEcommerceItems([{
                item_LiheId: store.getters.getBooking?.liheId || '',
                item_LiheName: store.getters.getBooking?.liheName || '',
                item_PrestId: lastOption.prestId,
                item_PrestName: lastOption.name || '',
                item_price_ati: lastOption.sellingPrice || 0,
                item_price_tf: lastOption.purchasePrice || 0,
                item_quantity: lastOption.quantity || 0,
                item_category: ''
            }]);
        }
    });
}

// 20. Remove From Cart
export function trackRemoveFromCart(): void {
    trackEvent('remove_from_cart', (b) => {
        const removed = store.getters.getLastUnselectedOption
        if (removed) {
            b.addEcommerceItems([{
                item_LiheId: removed.prestId,
                item_LiheName: removed.name || '',
                item_category: '',
                item_price_ati: removed.sellingPrice || 0,
                item_price_tf: removed.purchasePrice || 0
            }]);
        }
    });
}

// 21. Age Change / Cart Reset
export function trackAgeChangeCartReset(): void {
    trackEvent('age_change_cart_reset', (b) => {
        trackCartContext(b);
    });
}

// 22. Product Unavailable
export function trackProductUnavailable(): void {
    trackEvent('product_unavailable', (b) => {
        trackCartContext(b);
    });
}

// 23. Overbooking
export function trackOverbooking(): void {
    trackEvent('overbooking', (b) => {
        trackCartContext(b);
    });
}

// 24. Payment Error
export function trackPaymentError(): void {
    trackEvent('payment_error', (b) => {
        const booking = store.getters.getBooking;
        b
            .param('transaction_id', booking?.boReference || '')
            .param('cart_id', booking?.boReference || '');
    });
}

// 25. Age Change / Cart Reset
export function trackAddParticipant(): void {
    trackEvent('add_participant', (b) => {
        trackCartContext(b);
    });
}
// 26. Remove Participant
export function trackRemoveParticipant(): void {
    trackEvent('remove_participant', (b) => {
        trackCartContext(b);
    });
}

// Shared cart context mapping
function trackCartContext(b: DataLayerEventBuilder) {
    const booking = store.getters.getBooking;
    b
        .param('cart_id', booking?.boReference || '')
        .param('cart_amount_ati', booking?.sellingPrice || 0)
        .param('cart_amount_tf', booking?.purchasePrice || 0)
        .param('cart_amount_ati_without_fees', calculateWithoutFees() || 0)
        .param('cart_amount_tf_without_fees', calculateWithoutFees() || 0)
        .param('cart_discount_amount_ati', '')
        .param('fees', calculateFees() || 0)
        .param('promocode', booking?.promocode?.code || '');
    trackCartItems(b);
}

function calculateWithoutFees() {
    const fees = calculateFees()
    return store.getters.getBooking.sellingPrice - fees;
}

function calculateFees() {
    const booking = store.getters.getBooking;
    const fees = booking?.options.find(o => o.id === 'Total fees')?.sellingPrice || 0;
    return fees;
}